import { useEffect, useRef, useState } from "react";

type Pt = [number, number];
/** Strokes are kept in 0–1 coordinates so the pad can be redrawn at any size (rotation, resize). */
type Stroke = Pt[];

const INK = "#0f2a4f";
const EXPORT_W = 720;

function paint(ctx: CanvasRenderingContext2D, strokes: Stroke[], w: number, h: number, lineWidth: number) {
  ctx.lineWidth = lineWidth; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = INK; ctx.fillStyle = INK;
  for (const s of strokes) {
    if (!s.length) continue;
    if (s.length === 1) { ctx.beginPath(); ctx.arc(s[0]![0] * w, s[0]![1] * h, lineWidth / 2, 0, Math.PI * 2); ctx.fill(); continue; }
    ctx.beginPath(); ctx.moveTo(s[0]![0] * w, s[0]![1] * h);
    for (const [x, y] of s.slice(1)) ctx.lineTo(x * w, y * h);
    ctx.stroke();
  }
}

/** Fixed-size PNG (small enough for any phone/iPad, same look on every device). */
function exportPng(strokes: Stroke[], bg: HTMLImageElement | null, aspect: number): string {
  const EXPORT_H = Math.round(EXPORT_W * Math.min(1, Math.max(0.2, aspect || 1 / 3)));
  const c = document.createElement("canvas"); c.width = EXPORT_W; c.height = EXPORT_H;
  const ctx = c.getContext("2d")!;
  if (bg?.complete && bg.naturalWidth) ctx.drawImage(bg, 0, 0, EXPORT_W, EXPORT_H);
  paint(ctx, strokes, EXPORT_W, EXPORT_H, 4);
  return c.toDataURL("image/png");
}

/**
 * Finger / Apple Pencil / mouse signature pad. Calls onChange with a PNG data URL after every
 * stroke (or null when cleared).
 *
 * Built for phones: native non-passive touch listeners stop iOS from scrolling or cancelling
 * the stroke, the canvas re-sizes itself whenever its box changes (it used to size once at
 * mount, so a pad laid out late or rotated could stay blank), and a stroke cancelled by the
 * browser is still kept.
 */
export function SignaturePad({ value, onChange, disabled }: { value: string | null; onChange: (png: string | null) => void; disabled?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Stroke[]>([]);
  const current = useRef<{ id: number; stroke: Stroke } | null>(null);
  const background = useRef<HTMLImageElement | null>(null);
  /** The PNG we last handed up; when it comes back as `value` it is not a new signature. */
  const emitted = useRef<string | null>(null);
  const [hasInk, setHasInk] = useState(!!value);
  const props = useRef({ onChange, disabled });
  props.current = { onChange, disabled };

  const redraw = () => {
    const c = canvas.current; if (!c) return;
    const ratio = window.devicePixelRatio || 1, w = c.clientWidth, h = c.clientHeight;
    if (!w || !h) return;
    if (c.width !== Math.round(w * ratio) || c.height !== Math.round(h * ratio)) { c.width = Math.round(w * ratio); c.height = Math.round(h * ratio); }
    const ctx = c.getContext("2d")!;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (background.current?.complete && background.current.naturalWidth) ctx.drawImage(background.current, 0, 0, w, h);
    paint(ctx, current.current ? [...strokes.current, current.current.stroke] : strokes.current, w, h, 2.4);
  };

  // A saved signature (reopened report) is shown as the background image.
  useEffect(() => {
    if (value && value === emitted.current) return;
    strokes.current = []; current.current = null;
    if (!value) { background.current = null; redraw(); setHasInk(false); return; }
    if (background.current?.src === value) return;
    const img = new Image(); img.onload = redraw; img.src = value; background.current = img; setHasInk(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const c = canvas.current!;
    const ro = new ResizeObserver(() => redraw());
    ro.observe(c);
    const pos = (e: PointerEvent): Pt => { const r = c.getBoundingClientRect(); return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))]; };
    const finish = () => {
      const cur = current.current; if (!cur) return;
      current.current = null;
      strokes.current.push(cur.stroke);
      redraw(); setHasInk(true);
      emitted.current = exportPng(strokes.current, background.current, c.clientHeight / c.clientWidth);
      props.current.onChange(emitted.current);
    };
    const down = (e: PointerEvent) => {
      if (props.current.disabled || (e.pointerType === "mouse" && e.button !== 0)) return;
      e.preventDefault();
      if (current.current) finish();
      try { c.setPointerCapture(e.pointerId); } catch { /* not all browsers */ }
      current.current = { id: e.pointerId, stroke: [pos(e)] };
      redraw();
    };
    const move = (e: PointerEvent) => {
      const cur = current.current; if (!cur || cur.id !== e.pointerId) return;
      e.preventDefault();
      const evs = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [];
      for (const ev of evs.length ? evs : [e]) cur.stroke.push(pos(ev));
      redraw();
    };
    const up = (e: PointerEvent) => { if (current.current?.id === e.pointerId) finish(); };
    const block = (e: TouchEvent) => { if (!props.current.disabled) e.preventDefault(); };
    c.addEventListener("pointerdown", down);
    c.addEventListener("pointermove", move);
    c.addEventListener("pointerup", up);
    c.addEventListener("pointercancel", up);
    c.addEventListener("lostpointercapture", up);
    c.addEventListener("touchstart", block, { passive: false });
    c.addEventListener("touchmove", block, { passive: false });
    return () => {
      ro.disconnect();
      c.removeEventListener("pointerdown", down); c.removeEventListener("pointermove", move); c.removeEventListener("pointerup", up);
      c.removeEventListener("pointercancel", up); c.removeEventListener("lostpointercapture", up);
      c.removeEventListener("touchstart", block); c.removeEventListener("touchmove", block);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = () => {
    strokes.current = []; current.current = null; background.current = null;
    emitted.current = null; redraw(); setHasInk(false); onChange(null);
  };
  return (
    <div className={`sigpad ${disabled ? "disabled" : ""}`}>
      <canvas ref={canvas} style={{ touchAction: "none" }} aria-label="Signature pad" />
      <div className="row between">
        <span className="muted small">{disabled ? (hasInk ? "Signed" : "Not signed") : hasInk ? "✓ Signature captured" : "Sign in the box with your finger"}</span>
        {!disabled && <button type="button" onClick={clear}>Clear</button>}
      </div>
    </div>
  );
}
